import { useQuery, UseQueryResult, keepPreviousData } from '@tanstack/react-query';
import { companyApi, SafetyStaffCoverageRow } from 'shared/services/companyApi';
import { Company } from 'shared/types';
import { ApiResponse } from 'shared/services/apiClient';
import { queryKeys } from '../queryConfig';

/**
 * Filter parameters for companies list query
 */
export interface CompaniesFilters {
  search?: string;
  status?: 'all' | 'active' | 'inactive' | 'suspended';
  industry?: string;
  companyType?: 'cliente' | 'proveedor' | 'all';
  missingSafetyStaff?: boolean;
  accountExecutiveId?: string;
  accountExecutiveRole?: 'titular' | 'suplente';
  includeAccountExecutive?: boolean;
  page?: number;
  limit?: number;
}

/**
 * Pagination info returned from API
 */
interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

/**
 * Response structure from companies API (transformed by query)
 */
interface CompaniesResponse {
  success: boolean;
  data: Company[];
  pagination: PaginationInfo;
  error?: string;
}

/**
 * Stats structure for companies
 */
interface CompanyStats {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
}

/**
 * Hook to fetch companies with filters
 *
 * Features:
 * - Automatic caching (5 min TTL)
 * - Deduplication (multiple components share same request)
 * - Background refetching when stale
 * - Previous data shown while fetching new data (keepPreviousData)
 * - Automatic error handling
 *
 * @param filters - Filter parameters for the query
 * @returns React Query result with companies data, loading state, error state
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useCompanies({
 *   search: 'tech',
 *   status: 'active',
 *   page: 1,
 *   limit: 10
 * });
 *
 * const companies = data?.data || [];
 * const total = data?.pagination?.total || 0;
 * ```
 */
export const useCompanies = (
  filters: CompaniesFilters = {}
): UseQueryResult<CompaniesResponse, Error> => {
  // Build query params - only include non-empty values
  const queryParams = {
    page: filters.page || 1,
    limit: filters.limit || 10,
    ...(filters.search && { search: filters.search }),
    ...(filters.status && filters.status !== 'all' && { status: filters.status }),
    ...(filters.industry && { industry: filters.industry }),
    ...(filters.companyType && filters.companyType !== 'all' && { companyType: filters.companyType }),
    ...(filters.missingSafetyStaff && { missingSafetyStaff: filters.missingSafetyStaff }),
    ...(filters.accountExecutiveId && { accountExecutiveId: filters.accountExecutiveId }),
    ...(filters.accountExecutiveRole && { accountExecutiveRole: filters.accountExecutiveRole }),
    ...(filters.includeAccountExecutive && { includeAccountExecutive: filters.includeAccountExecutive }),
  };

  return useQuery({
    queryKey: queryKeys.companies.list(queryParams),
    queryFn: async () => {
      const response = await companyApi.getAll(queryParams);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch companies');
      }

      // Filter out "Particular" company (business rule)
      const filteredData = (response.data || []).filter(
        (company: Company) => company.name.toLowerCase() !== 'particular'
      );

      return {
        ...response,
        data: filteredData,
        pagination: response.pagination || {
          total: filteredData.length,
          page: queryParams.page,
          limit: queryParams.limit,
          pages: Math.ceil(filteredData.length / queryParams.limit),
        },
      };
    },
    staleTime: 5 * 60 * 1000,        // 5 minutes - data considered fresh
    gcTime: 10 * 60 * 1000,          // 10 minutes - keep in memory
    placeholderData: keepPreviousData, // Show old data while fetching new
    retry: 1,
  });
};

/**
 * Hook to fetch company stats
 *
 * Stats are cached longer (15 min) since they don't change often.
 * Useful for dashboard cards and summary displays.
 *
 * @returns React Query result with stats data
 *
 * @example
 * ```tsx
 * const { data: statsData } = useCompanyStats();
 * const stats = statsData?.data || { total: 0, active: 0, inactive: 0, suspended: 0 };
 * ```
 */
export const useCompanyStats = (): UseQueryResult<ApiResponse<CompanyStats>, Error> => {
  return useQuery({
    queryKey: queryKeys.companies.stats(),
    queryFn: async () => {
      const response = await companyApi.getGeneralStats();

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch company stats');
      }

      return response;
    },
    staleTime: 15 * 60 * 1000,       // 15 minutes - stats cached longer
    gcTime: 20 * 60 * 1000,          // 20 minutes in memory
    retry: 1,
  });
};

/**
 * Hook to fetch a single company by ID
 *
 * @param id - Company ID
 * @param enabled - Whether the query should run (default true)
 * @returns React Query result with company data
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useCompany('123');
 * ```
 */
export const useCompany = (
  id: string,
  enabled = true
): UseQueryResult<ApiResponse<Company>, Error> => {
  return useQuery({
    queryKey: queryKeys.companies.detail(id),
    queryFn: async () => {
      const response = await companyApi.getById(id);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch company');
      }

      return response;
    },
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

/**
 * Hook to fetch companies for dropdown/select components
 *
 * Cached longer since dropdown options change rarely.
 *
 * @returns React Query result with select options
 */
/**
 * Hook to fetch the per-Soporte-Monitor company coverage summary
 * (titular/suplente counts). Powers the super_admin coverage dashboard on
 * the unlocked /companies page — cached like the other admin summaries.
 */
export const useSafetyStaffCoverage = (
  enabled = true,
): UseQueryResult<SafetyStaffCoverageRow[], Error> => {
  return useQuery({
    queryKey: [...queryKeys.companies.all, 'safety-staff-coverage'] as const,
    queryFn: () => companyApi.getSafetyStaffCoverage(),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });
};

export const useCompaniesSelect = (enabled: boolean = true): UseQueryResult<
  ApiResponse<Array<{ _id: string; name: string }>>,
  Error
> => {
  return useQuery({
    queryKey: [...queryKeys.companies.all, 'select'],
    queryFn: async () => {
      const response = await companyApi.getSelect();

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch companies for select');
      }

      return response;
    },
    enabled,
    staleTime: 10 * 60 * 1000,       // 10 minutes - select options cached longer
    gcTime: 15 * 60 * 1000,
    retry: 1,
  });
};
